import { packageJsonRepairRules } from "./repairs/packageJsonRepair.js";
import { podInstallRepairRules } from "./repairs/podInstallRepair.js";
import { lockfileRepairRules } from "./repairs/lockfileRepair.js";
import { gradleRepairRules } from "./repairs/gradleRepair.js";
import { cacheRepairRules } from "./repairs/cacheRepair.js";

export interface RepairAction {
  id: string;
  title: string;
  description: string;
  safe: boolean;
  executed: boolean;
  success: boolean;
  details?: string;
}

export interface RepairRule {
  id: string;
  title: string;
  description: string;
  safe: boolean;
  detect(cwd: string): Promise<boolean>;
  repair(cwd: string): Promise<{ success: boolean; details?: string }>;
}

import { TaskRunner } from "../core/taskRunner.js";
import { Task } from "../core/task.js";

// Registry of all rules
const repairRulesRegistry: RepairRule[] = [
  ...packageJsonRepairRules,
  ...podInstallRepairRules,
  ...lockfileRepairRules,
  ...gradleRepairRules,
  ...cacheRepairRules,
];

/**
 * Scans the project directory and returns a list of planned repairs that are needed.
 */
export async function planRepairs(cwd: string): Promise<RepairAction[]> {
  const planned: RepairAction[] = [];

  for (const rule of repairRulesRegistry) {
    try {
      const needed = await rule.detect(cwd);
      if (needed) {
        planned.push({
          id: rule.id,
          title: rule.title,
          description: rule.description,
          safe: rule.safe,
          executed: false,
          success: false,
        });
      }
    } catch {
      // Gracefully ignore detection failures for specific rules
    }
  }

  return planned;
}

/**
 * Sequentially executes a list of repair actions.
 */
export async function executeRepairs(
  cwd: string,
  actionsToRun: RepairAction[],
  onProgress?: (action: RepairAction, step: "start" | "end") => void,
  verbose: boolean = false,
  json: boolean = false
): Promise<RepairAction[]> {
  const runner = new TaskRunner({ verbose, json });

  const tasks: Task[] = actionsToRun.map((action) => {
    return {
      id: action.id,
      title: action.title,
      abortOnFailure: false,
      execute: async () => {
        if (onProgress) {
          onProgress(action, "start");
        }
        const rule = repairRulesRegistry.find((r) => r.id === action.id);
        if (!rule) {
          throw new Error(`Repair rule matching ID '${action.id}' was not found in registry.`);
        }
        try {
          const res = await rule.repair(cwd);
          (action as any).details = res.details;
          if (onProgress) {
            onProgress({ ...action, success: res.success, details: res.details }, "end");
          }
          if (!res.success) {
            throw new Error(res.details || "Repair failed");
          }
        } catch (err: any) {
          if (onProgress) {
            onProgress({ ...action, success: false, details: err?.message }, "end");
          }
          throw err;
        }
      },
    };
  });

  runner.addTasks(tasks);
  const runnerResults = await runner.run();

  return runnerResults.map((r, index) => {
    const action = actionsToRun[index]!;
    return {
      ...action,
      executed: true,
      success: r.success,
      details: (action as any).details || r.error || "Completed successfully.",
    };
  });
}
